import type * as Sentry from "@sentry/react";

type MutableRecord = Record<string, unknown>;

const REDACTED_EMAIL = "[REDACTED_EMAIL]";
const REDACTED_ID = "[REDACTED_ID]";
const REDACTED_TOKEN = "[REDACTED_TOKEN]";
const REDACTED_STORAGE_PATH = "chat_attachments/[REDACTED_USER_ID]/[REDACTED_FILE]";

const SENSITIVE_FIELD_REPLACEMENTS: Record<string, string> = {
  audioUrl: "[REDACTED_AUDIO_URL]",
  chatId: "[REDACTED_CHAT_ID]",
  customerId: "[REDACTED_CUSTOMER_ID]",
  email: REDACTED_EMAIL,
  fileName: "[REDACTED_FILE]",
  filename: "[REDACTED_FILE]",
  imageUrl: "[REDACTED_IMAGE_URL]",
  uid: "[REDACTED_USER_ID]",
  userEmail: REDACTED_EMAIL,
  userId: "[REDACTED_USER_ID]",
};

const getErrorName = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || !("name" in error)) return undefined;
  const name = (error as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
};

const getErrorStringProperty = (error: unknown, key: string): string | undefined => {
  if (!error || typeof error !== "object" || !(key in error)) return undefined;
  const value = (error as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() ? value : undefined;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== "object" || !("message" in error)) return String(error ?? "");
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" ? message : String(message ?? "");
};

export const shouldDropExpectedSentryError = (error: unknown, context?: string): boolean => {
  const name = getErrorName(error);
  const message = getErrorMessage(error).toLowerCase();
  const errorKind = getErrorStringProperty(error, "errorKind");

  if (name === "AbortError") return true;
  if (errorKind === "usage_limit_reached") return true;
  if (context === "api:sendChatMessage:http:403" && message.includes("api 403: usage limit reached")) return true;
  if (message.includes("failed to get document because the client is offline")) return true;
  if (message.includes("the client is offline") && context?.startsWith("userService:")) return true;

  return false;
};

export const redactSensitiveString = (value: string): string => (
  value
    .replace(/([?&](?:token|x-goog-signature|x-goog-credential)=)[^&\s'")]+/gi, `$1${REDACTED_TOKEN}`)
    .replace(/chat_attachments\/[^/\\\s'")]+\/[^\\\s'")?]+/g, REDACTED_STORAGE_PATH)
    .replace(/\b(users|chats|customers)\/[A-Za-z0-9_-]{12,}\b/g, `$1/${REDACTED_ID}`)
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, REDACTED_EMAIL)
);

const isRecord = (value: unknown): value is MutableRecord => (
  Boolean(value) && typeof value === "object" && !Array.isArray(value)
);

const redactObject = (value: unknown, depth = 0): unknown => {
  if (depth > 6) return "[REDACTED_DEEP_OBJECT]";
  if (typeof value === "string") return redactSensitiveString(value);
  if (Array.isArray(value)) return value.map((item) => redactObject(item, depth + 1));
  if (!isRecord(value)) return value;

  for (const [key, item] of Object.entries(value)) {
    if (Object.prototype.hasOwnProperty.call(SENSITIVE_FIELD_REPLACEMENTS, key)) {
      value[key] = SENSITIVE_FIELD_REPLACEMENTS[key];
      continue;
    }

    value[key] = redactObject(item, depth + 1);
  }

  return value;
};

export const redactSentryBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = redactSensitiveString(breadcrumb.message);
  }

  if (breadcrumb.data) {
    redactObject(breadcrumb.data);
  }

  return breadcrumb;
};

export const redactSentryEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  if (typeof event.message === "string") {
    event.message = "frontend_error";
  }

  if (event.logentry?.message) {
    event.logentry.message = "frontend_error";
  }

  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === "string") {
      exception.value = "frontend_error";
    }
  });

  delete event.user;

  if (event.request?.url) {
    event.request.url = redactSensitiveString(event.request.url.split(/[?#]/, 1)[0]);
  }

  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.env;
    delete event.request.headers;
    delete event.request.query_string;
  }

  delete event.breadcrumbs;

  if (event.extra) {
    event.extra = {};
  }

  return event;
};

export const sanitizeMonitoringMessage = (message: unknown): string => {
  return redactSensitiveString(typeof message === "string" ? message : String(message ?? ""));
};
